import { CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import { unitOptionsSchema } from '../../utils';
import { createDefinitionSchema } from '../../utils/helpers';
import { createThresholdOptionsSchema, stepOptionsSchema } from '../../utils/threshold';
/**
 * Type for the spec-driven `DashboardPanel` variant of the status history panel.
 */
export interface StatusHistoryPanelSpec extends z.TypeOf<typeof statusHistoryPanelSpecSchema> {}

const barFormatThresholdOptionsSchema = createThresholdOptionsSchema(stepOptionsSchema);

export interface StatusHistoryStepOption extends z.TypeOf<typeof stepOptionsSchema> {}
export interface StatusHistoryThresholdOptions extends z.TypeOf<typeof barFormatThresholdOptionsSchema> {}

export const DEFAULT_VISUAL_DENSITY = 4;

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the status history panel.
 */
export const statusHistoryPanelSpecSchema = z.object({
  unit: unitOptionsSchema.optional().default({ kind: 'Decimal' }),
  thresholds: barFormatThresholdOptionsSchema.optional().default({}),
  visual: z
    .object({
      density: z.number().gte(0).lte(10).default(DEFAULT_VISUAL_DENSITY),
    })
    .optional()
    .default({ density: DEFAULT_VISUAL_DENSITY }),
});

/**
 * The schema for a StatusHistory `DashboardPanel` definition.
 */
export interface StatusHistoryPanelDefinition extends z.TypeOf<typeof statusHistoryPanelDefinitionSchema> {}
// statusHistoryPanelSchema is a schema for a StatusHistory panel, which essentially implements Definition<StatusHistoryPanelSpec, 'StatusHistory'>.
export const statusHistoryPanelDefinitionSchema = createDefinitionSchema('StatusHistory', statusHistoryPanelSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the status history panel.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const statusHistoryPanelVizSchema = statusHistoryPanelSpecSchema;

/**
 * Base type for the props-driven `Viz` variant of the status history panel generated from the Zod schema and
 * converting all snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface StatusHistoryPanelPropsBase
  extends CamelCasedPropertiesDeep<z.TypeOf<typeof statusHistoryPanelVizSchema>> {}
